import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material'
import {FormattedMessage, useIntl} from 'react-intl'
import {useCallback, useEffect, useState} from 'react'
import {
  FileUploadData,
  FileUploadView,
  ResponseError,
  useResolver,
  useTest
} from '@enisco/mcc-react-lib'

export interface UploadDialogProps {
  open: boolean
  onClose: () => void
}

const licenseFileTypes = ['.yaml', '.yml', '.license']

const encodeLicenseFile = (file: Blob): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsText(file)
    reader.onload = () => {
      const contentStr = reader.result?.toString()
      if (!contentStr) {
        reject('invalid license content')
        return
      }
      const bytes = new TextEncoder().encode(contentStr)
      const binString = Array.from(bytes, (byte) =>
        String.fromCodePoint(byte),
      ).join("");
      resolve(btoa(binString))
    }
    reader.onerror = () => {
      reject(reader.error)
    }
  })
}


const LicenseUploadDialog = (props: UploadDialogProps) => {
  const [files, setFiles] = useState<FileUploadData[]>([])
  const [error, setError] = useState<string | null>(null)
  const [additionalMessages, setAdditionalMessages] = useState<string[]>([])
  const resolver = useResolver()
  const isTestMode = useTest()
  const intl = useIntl()

  const handleClose = useCallback(() => {
    props.onClose()
  }, [])

  const handleCancel = useCallback(() => {
    props.onClose()
  }, [])

  useEffect(() => {
    if (props.open) {
      return
    }
    setFiles([])
    setError(null)
  }, [props.open])

  const uploadLicense = async (file: Blob) => {
    const content = await encodeLicenseFile(file)
    await resolver('mcc-portal-api', '/license/upload', {
      options: {
        method: 'POST',
        headers: {'Content-Type': 'application/base64'},
        body: content
      },
      mockedResponse: isTestMode ? JSON.stringify({}) : undefined
    })
  }

  const handleFilesChanged = (data: FileUploadData[]) => {
    setError(null)
    setFiles(data)
  }

  const handleAccept = useCallback(async () => {
    try {
      await uploadLicense(files[0].file)
      props.onClose()
    } catch (e) {
      let message = 'upload failed'
      const messages = []
      if (e instanceof ResponseError) {
        const err = await e.response.json()
        if(!('type' in err)) {
          message = intl.formatMessage(
            {
              id: 'LicenseUploadDialog.Error.ResponseError',
              defaultMessage:
                'Failed to upload license, server returned status {code}: {message}'
            },
            {
              code: `${e.response.status} ${e.response.statusText}`,
              message: err
            }
          )
          return
        }

        switch (err.type) {
          case 'verification_failed':
            message = intl.formatMessage({
              id: 'LicenseUploadDialog.Error.VerificationFailed',
              defaultMessage: 'License verification failed'
            })
            messages.push(...(err.message as string).split('\n'))
            break
          case 'parsing_failed':
            message = intl.formatMessage({
              id: 'LicenseUploadDialog.Error.ParsingFailed',
              defaultMessage: 'Failed to parse license'
            })
            break
          case 'license_expired':
            message = intl.formatMessage({
              id: 'LicenseUploadDialog.Error.LicenseExpired',
              defaultMessage: 'License is already expired'
            })
            break
          default:
            message = intl.formatMessage(
              {
                id: 'LicenseUploadDialog.Error.ResponseError',
                defaultMessage:
                  'Failed to upload license, server returned status {code}: {message}'
              },
              {
                code: `${e.response.status} ${e.response.statusText}`,
                message: err.message
              }
            )
        }
      } else if (e) {
        message = intl.formatMessage(
          {
            id: 'LicenseUploadDialog.Error.Default',
            defaultMessage: 'Failed to upload license: {message}'
          },
          {
            message: e.toString()
          }
        )
      }
      setError(message)
      setAdditionalMessages(messages)
    }
  }, [files, resolver])

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth>
      <DialogTitle>
        <FormattedMessage
          id={'LicenseUploadDialog.Title'}
          defaultMessage='Upload License'
        />
      </DialogTitle>
      <DialogContent>
        <FileUploadView
          maxFiles={1}
          onFileSelected={handleFilesChanged}
          allowedFileTypes={licenseFileTypes}
          files={files}
        />
        <br/>
        {error && <Alert severity={'error'}>
          {error}
          {additionalMessages.map(msg => (
            <p>{msg}</p>
          ))}
        </Alert>}

      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>
          <FormattedMessage id={'Dialog.Cancel'} defaultMessage='Cancel' />
        </Button>
        <Button autoFocus onClick={handleAccept} disabled={files.length != 1}>
          <FormattedMessage
            id={'LicenseUploadDialog.Button.Import'}
            defaultMessage='Import'
          />
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LicenseUploadDialog
